<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import type { WatchStopHandle } from 'vue'
import type { PopupInterface } from '~~/server/types/popup'

const props = defineProps<{ popup: PopupInterface }>()
const emit = defineEmits<{ result: [value: true] }>()
const { locale, t } = useI18n()
const route = useRoute()

const description = computed(() => props.popup.descriptions[locale.value] || props.popup.descriptions.bg || '')
const imageLanguage = computed(() => props.popup.images[locale.value]?.url ? locale.value : 'bg')
const image = computed(() => props.popup.images[imageLanguage.value]?.url || '')
const imageLink = computed(() => props.popup.image_links[imageLanguage.value] || '')
const codeLanguage = computed(() => props.popup.custom_html[locale.value] ? locale.value : 'bg')
const customHtml = computed(() => props.popup.custom_html[codeLanguage.value] || '')
const customCss = computed(() => props.popup.custom_css[codeLanguage.value] || '')

let stopCss: WatchStopHandle | null = null
let style: HTMLStyleElement | null = null

onMounted(() => {
  if (props.popup.design === 'custom') {
    stopCss = watch(customCss, (css) => {
      if (!style) {
        style = document.createElement('style')
        document.head.appendChild(style)
      }
      style.textContent = css
    }, { immediate: true })
  }
})

watch(() => route.fullPath, () => emit('result', true))

onBeforeUnmount(() => {
  stopCss?.()
  style?.remove()
})
</script>

<template>
  <div class="popup-dialog">
    <div class="popup-dialog__actions">
      <button type="button" class="popup-dialog__close" :aria-label="t('close')" @click="emit('result', true)">×</button>
    </div>

    <div class="popup-dialog__body">
      <div v-if="popup.design === 'editor'" class="content" v-html="description" />

      <div v-else-if="popup.design === 'image'" class="popup-dialog__image">
        <a v-if="imageLink" :href="imageLink" @click="emit('result', true)">
          <img :src="image" :alt="popup.name">
        </a>
        <img v-else :src="image" :alt="popup.name">
      </div>

      <div v-else class="popup-dialog__custom" v-html="customHtml" />
    </div>
  </div>
</template>

<style scoped>
.popup-dialog {
  display: flex;
  flex-direction: column;
  max-height: calc(85vh - 80px);
  max-height: calc(85dvh - 80px);
  min-width: 0;
}

.popup-dialog__actions {
  display: flex;
  flex: none;
  justify-content: flex-end;
  margin: -8px -8px 8px 0;
}

.popup-dialog__body {
  min-height: 0;
  overflow: auto;
  overflow-wrap: anywhere;
}

.popup-dialog__close {
  border: 0;
  background: transparent;
  color: var(--el-text-color-regular);
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  min-height: 32px;
  min-width: 32px;
}

.popup-dialog__image,
.popup-dialog__image a {
  display: block;
  text-align: center;
}

.popup-dialog__image img {
  max-width: 100%;
  max-height: 65dvh;
  object-fit: contain;
}

.popup-dialog__body :deep(img),
.popup-dialog__body :deep(video),
.popup-dialog__body :deep(iframe) {
  max-width: 100%;
}

@media (max-width: 568px) {
  .popup-dialog__close {
    min-height: 44px;
    min-width: 44px;
  }
}
</style>
